// @ts-check
// Estado das novidades na SPA (contrato 08-contrato-novidades.md, seção 4): a versão anunciada vem da marca da pessoa
// no /auth/me; o relógio segura a abertura automática até a tela assentar; a superfície (balão, cartão ou nada) sai da
// regra pura de client/src/lib/release-notes-display.js com o que a tela tem agora.
import { computed, signal } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import { locationState, routeState } from '../../app/router.js';
import { currentWorkspaceMemberState, sessionState } from '../../data/metadata-store.js';
import { APP_VERSION } from '../../../../shared/app-version.js';
import { pendingReleaseAnnouncement } from '../../../../shared/release-notes/release-notes.js';
import { isSupportMember } from '../../../../shared/support/support-member.js';
import { resolveReleaseNotesSurface } from '../../lib/release-notes-display.js';
import { useIsMobile } from '../../ui/hooks.js';
import { isNavigationDrawerExpandedState } from '../navigation/navigation-state.js';
import { navigationDraftState } from '../navigation/navigation-editor.js';
import { useIsMobileBottomChromeHidden } from '../navigation/mobile-navigation-bar.js';
import { activeSelectionTargetState } from '../command-menu/command-state.js';

// espera depois de a tela montar: o balão não aparece no meio do carregamento nem rouba a atenção do primeiro clique
const AUTO_OPEN_DELAY_MILLISECONDS = 1500;
// com um diálogo aberto, a superfície confere de novo nesse intervalo para aparecer quando ele fechar
const MODAL_RECHECK_MILLISECONDS = 1000;

/** O suporte (painel interno) não recebe o aviso: nem pela sessão nem pelo membro de suporte. */
export const isReleaseNotesSupportState = computed(() => Boolean(/** @type {any} */ (sessionState.value)?.supportAccess)
  || isSupportMember(currentWorkspaceMemberState.value));

/** Versão anunciada (entrada do catálogo) ou null: sem marca no /auth/me (suporte, leitura que falhou) não anuncia. */
export const releaseNotesAnnouncementState = computed(() => {
  const releaseNotes = /** @type {any} */ (sessionState.value)?.releaseNotes;
  if (!releaseNotes || isReleaseNotesSupportState.value) return null;
  return pendingReleaseAnnouncement({ seenVersion: releaseNotes.lastSeenVersion, currentVersion: APP_VERSION });
});

/** Fica true 1,5 s depois de a tela montar com a aba visível; com a aba oculta, espera ela voltar. */
export const isReleaseNotesAutoOpenReadyState = signal(false);

export function useReleaseNotesAutoOpenClock() {
  useEffect(() => {
    if (isReleaseNotesAutoOpenReadyState.peek()) return undefined;
    /** @type {ReturnType<typeof setTimeout> | null} */
    let timer = null;
    const start = () => {
      if (timer !== null || document.visibilityState !== 'visible') return;
      timer = setTimeout(() => {
        isReleaseNotesAutoOpenReadyState.value = true;
      }, AUTO_OPEN_DELAY_MILLISECONDS);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') start();
      else if (timer !== null && !isReleaseNotesAutoOpenReadyState.peek()) {
        clearTimeout(timer);
        timer = null;
      }
    };
    start();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      if (timer !== null) clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}

const hasOpenModalDialog = () => Boolean(document.querySelector('[role="dialog"][aria-modal="true"]'));

/**
 * Superfície das novidades agora. `surface` é a resposta da regra; o cartão fica montado também quando só a barra
 * flutuante sumiu (rolagem, seleção), para sumir e voltar junto com ela, então `isCardMounted` ignora esses dois e
 * `isCardHidden` diz se ele está escondido.
 */
export function useReleaseNotesSurface() {
  const isMobile = useIsMobile();
  const isBottomChromeHidden = useIsMobileBottomChromeHidden();
  const [, setModalCheck] = useState(0);
  const announcement = releaseNotesAnnouncementState.value;
  const pathname = locationState.value.pathname;
  const isSettings = pathname === '/settings' || pathname.startsWith('/settings/');
  const hasOpenModal = Boolean(announcement) && hasOpenModalDialog();
  useEffect(() => {
    if (!hasOpenModal) return undefined;
    const timer = setInterval(() => setModalCheck((count) => count + 1), MODAL_RECHECK_MILLISECONDS);
    return () => clearInterval(timer);
  }, [hasOpenModal]);
  /** @type {import('../../lib/release-notes-display.js').ReleaseNotesSurfaceInput} */
  const input = {
    hasAnnouncement: Boolean(announcement),
    isSupport: isReleaseNotesSupportState.value,
    isReady: isReleaseNotesAutoOpenReadyState.value,
    routeName: routeState.value.name,
    isMobile,
    // a gaveta de Configurações nunca vira trilho
    isDrawerExpanded: isSettings || isNavigationDrawerExpandedState.value,
    isEditingNavigation: !isSettings && Boolean(navigationDraftState.value),
    isBottomChromeHidden,
    hasActiveSelection: (activeSelectionTargetState.value?.count ?? 0) > 0,
    hasOpenModal,
  };
  const surface = resolveReleaseNotesSurface(input);
  const isCardMounted = resolveReleaseNotesSurface({ ...input, isBottomChromeHidden: false, hasActiveSelection: false }) === 'card';
  return { announcement, surface, isMobile, isCardMounted, isCardHidden: isCardMounted && surface !== 'card' };
}
